import { Box, Button, Chip, Divider, List, ListItem, ListItemText, Stack, Typography, Paper } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ModalComponent from "./Modal/Modal";

const Section = ({ color, title, total, hint, children }) => {
  return (
    <Box mb={2}>
      <Paper
        variant="outlined"
        sx={(theme) => ({
          p: 1.5,
          borderRadius: 2,
          borderColor: alpha(theme.palette[color].main, 0.25),
          backgroundColor: alpha(theme.palette[color].main, 0.06),
        })}
      >
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography fontWeight={600} sx={{ color: `${color}.dark` }}>
              {title}
            </Typography>

            {typeof total === "number" && (
              <Chip
                size="small"
                label={`Total: ${total}`}
                variant="outlined"
                sx={(theme) => ({
                  height: 22,
                  borderColor: alpha(theme.palette[color].main, 0.4),
                  color: theme.palette[color].main,
                })}
              />
            )}

            {hint && (
              <Typography fontSize={12} color="text.secondary">
                {hint}
              </Typography>
            )}
          </Stack>
        </Stack>

        {children}
      </Paper>
    </Box>
  );
};

const AssignResultsModal = ({ open, onClose, summary }) => {
  if (!summary) return null;

  const created = summary.created ?? [];
  const updated = summary.updated ?? [];
  const errors = summary.errors ?? [];

  const totals = {
    created: summary.raw?.created ?? created.length,
    updated: summary.raw?.updated ?? updated.length,
    errors: errors.length,
  };

  const isEmptySummary = totals.created === 0 && totals.updated === 0 && totals.errors === 0;

  return (
    <ModalComponent open={open} handleClose={onClose} width="620px">
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Resultado
      </Typography>

      {/* Creadas */}
      {totals.created > 0 && (
        <Section color="success" title="Asignaciones creadas" total={totals.created}>
          <List
            dense
            sx={(theme) => ({
              borderRadius: 1.5,
              maxHeight: 120,
              overflow: "auto",
              px: 0.5,
              "& .MuiListItem-root": { borderBottom: `1px dashed ${alpha(theme.palette.success.main, 0.16)}` },
              "& .MuiListItem-root:last-of-type": { borderBottom: "none" },
            })}
          >
            {created.map(({ user, assignapp_id: assignId }) => (
              <ListItem key={user.id}>
                <ListItemText
                  primary={user.name || "Sin nombre"}
                  secondary={[user.email, `asignación #${assignId}`].filter(Boolean).join(" · ")}
                  primaryTypographyProps={{ fontWeight: 600 }}
                  secondaryTypographyProps={{ fontSize: 12, color: "text.secondary" }}
                />
              </ListItem>
            ))}
          </List>
        </Section>
      )}

      {/* Actualizadas */}
      {totals.updated > 0 && (
        <Section color="info" title="Asignaciones actualizadas" total={totals.updated} hint="Se actualizaron las asignaciones.">
          {" "}
          <List
            dense
            sx={(theme) => ({
              borderRadius: 1.5,
              maxHeight: 120,
              overflow: "auto",
              px: 0.5,
              "& .MuiListItem-root": { borderBottom: `1px dashed ${alpha(theme.palette.info.main, 0.16)}` },
              "& .MuiListItem-root:last-of-type": { borderBottom: "none" },
            })}
          >
            {updated.map(({ user, assignapp_id: assignId }) => (
              <ListItem key={user.id}>
                <ListItemText
                  primary={user.name || "Sin nombre"}
                  secondary={[user.email, `asignación #${assignId}`].filter(Boolean).join(" · ")}
                  primaryTypographyProps={{ fontWeight: 600 }}
                  secondaryTypographyProps={{ fontSize: 12, color: "text.secondary" }}
                />
              </ListItem>
            ))}
          </List>
        </Section>
      )}

      {/* Errores */}
      {totals.errors > 0 && (
        <Section color="error" title="Con error" total={totals.errors}>
          <List
            dense
            sx={(theme) => ({
              borderRadius: 1.5,
              maxHeight: 120,
              overflow: "auto",
              px: 0.5,
              "& .MuiListItem-root": { borderBottom: `1px dashed ${alpha(theme.palette.error.main, 0.16)}` },
              "& .MuiListItem-root:last-of-type": { borderBottom: "none" },
            })}
          >
            {errors.map((err, idx) => {
              const user = err.user;
              const row = err.row;
              const who = user?.name || user?.email || (row ? `Fila ${row}` : "Desconocido");
              const sub = [user?.email, err.message].filter(Boolean).join(" · ");

              return (
                <ListItem key={user?.id || row || idx}>
                  <ListItemText
                    primary={who}
                    secondary={sub}
                    primaryTypographyProps={{ fontWeight: 600 }}
                    secondaryTypographyProps={{ fontSize: 12, color: "text.secondary" }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Section>
      )}

      {/* Nada para mostrar */}
      {isEmptySummary && (
        <Paper
          variant="outlined"
          sx={(theme) => ({
            p: 2,
            borderRadius: 2,
            borderColor: alpha(theme.palette.grey[500], 0.24),
            backgroundColor: alpha(theme.palette.grey[500], 0.06),
          })}
        >
          <Typography variant="body2" color="text.secondary">
            No hubo cambios para reportar.
          </Typography>
        </Paper>
      )}

      <Divider sx={{ my: 2 }} />
      <Box display="flex" justifyContent="flex-end" gap={1}>
        <Button onClick={onClose} variant="contained">
          Cerrar
        </Button>
      </Box>
    </ModalComponent>
  );
};

export default AssignResultsModal;
